/**
 * The school print REQUEST — the one selection shape both consumers of the
 * packet assembly share. The dialog builds it from its own state (to open
 * `/school/print`, or to assemble the same packet in place for a CSV
 * export); the page parses it back out of the URL. Keeping the URL codec
 * pure here means the dialog's URL and the page's reading of it can never
 * drift, and the CSV export sees exactly the selection Print would.
 *
 * URL: /school/print?locationId&from&to
 *        [&days=iso,…]                 subset of the range (dialog day pills)
 *        [&sections=records,passout]   default both
 *        [&grouping=grade|homeroom|alpha]  pass-out roster arrangement (PROD-1234;
 *                                      absent = legacy campus serve bucket)
 *        [&rosterAxis=grade|homeroom&rosterGroup=label…] selected roster groups (repeated param)
 *        [&mealtimes=LUNCH,…]          records + pass-out narrow (the dialog's Mealtimes row; legacy serve links)
 *        [&orderColumns=id,…]          Orders sheets scoped to these buckets
 *        [&orderSplit=bucket]          one Orders sheet PER bucket (PROD-1335)
 */

import { parseIsoDateParam } from '@/lib/cafeteria/format'
import { parseRosterGroupingParam, type RosterFilter, type RosterGrouping } from '@/lib/cafeteria/print-plan'
import { type Mealtime, parseMealtimeParam } from '@/lib/mealtime'
import { clampCustomRange, type SchoolPrintRange } from '@/lib/school-print/range'

export type SchoolPrintSectionKey = 'records' | 'passout' | 'orders'

export interface SchoolPrintRequest {
  locationId: string
  range: SchoolPrintRange
  /** Ascending subset of `range.days`; never empty. */
  selectedDays: readonly string[]
  sections: ReadonlySet<SchoolPrintSectionKey>
  grouping: RosterGrouping
  /** Serving-roster subset; independent of grouped versus A–Z arrangement. */
  rosterFilter?: RosterFilter
  /** Records and pass-out narrow; empty = every mealtime. */
  mealtimeFilter: ReadonlySet<Mealtime>
  /** Orders-sheet bucket subset; empty = every bucket. */
  orderColumnFilter: ReadonlySet<string>
  splitOrdersByBucket: boolean
}

export function parseSchoolPrintSections(raw: string | null): Set<SchoolPrintSectionKey> {
  const picked = new Set<SchoolPrintSectionKey>()
  for (const part of (raw ?? '').split(',')) {
    const trimmed = part.trim()
    if (trimmed === 'records' || trimmed === 'passout' || trimmed === 'orders') {
      picked.add(trimmed)
    }
  }
  return picked.size > 0 ? picked : new Set<SchoolPrintSectionKey>(['records', 'passout'])
}

/**
 * Null when `locationId` / `from` / `to` are missing or malformed — the page
 * renders its "open this page from a Print button" line. Every optional
 * param degrades to the default rather than erroring, so a hand-edited URL
 * still prints something.
 */
export function parseSchoolPrintRequest(searchParams: URLSearchParams): SchoolPrintRequest | null {
  const locationId = searchParams.get('locationId')
  const from = parseIsoDateParam(searchParams.get('from'))
  const to = parseIsoDateParam(searchParams.get('to'))
  if (!locationId || !from || !to) {
    return null
  }
  const range = clampCustomRange(from, to)
  if (!range) {
    return null
  }

  const requested = (searchParams.get('days') ?? '')
    .split(',')
    .map((raw) => parseIsoDateParam(raw.trim()))
    .filter((iso): iso is string => iso != null && range.days.includes(iso))
  const selectedDays = requested.length > 0 ? [...new Set(requested)].sort() : range.days

  const mealtimeFilter = new Set<Mealtime>()
  for (const part of (searchParams.get('mealtimes') ?? '').split(',')) {
    const mealtime = parseMealtimeParam(part.trim())
    if (mealtime) {
      mealtimeFilter.add(mealtime)
    }
  }

  const orderColumnFilter = new Set<string>()
  for (const part of (searchParams.get('orderColumns') ?? '').split(',')) {
    const trimmed = part.trim()
    if (trimmed) {
      orderColumnFilter.add(trimmed)
    }
  }

  return {
    locationId,
    range,
    selectedDays,
    sections: parseSchoolPrintSections(searchParams.get('sections')),
    grouping: parseRosterGroupingParam(searchParams.get('grouping')),
    ...parseRosterFilter(searchParams),
    mealtimeFilter,
    orderColumnFilter,
    // Any other value is the combined sheet, so a hand-edited URL degrades
    // rather than erroring.
    splitOrdersByBucket: searchParams.get('orderSplit') === 'bucket',
  }
}

function parseRosterFilter(params: URLSearchParams): { rosterFilter?: RosterFilter } {
  const axis = params.get('rosterAxis')
  const labels = new Set(params.getAll('rosterGroup').filter((label) => label.trim().length > 0))
  return (axis === 'grade' || axis === 'homeroom') && labels.size > 0 ? { rosterFilter: { axis, labels } } : {}
}

/** The `/school/print` URL for a request — the inverse of `parseSchoolPrintRequest`. */
export function schoolPrintRequestUrl(request: SchoolPrintRequest): string {
  const params = new URLSearchParams({
    locationId: request.locationId,
    from: request.range.from,
    to: request.range.to,
    sections: [...request.sections].join(','),
  })
  // Only a proper subset needs spelling out — every day of the range is
  // what the page assumes when the param is absent.
  if (request.selectedDays.length !== request.range.days.length) {
    params.set('days', request.selectedDays.join(','))
  }
  // 'buckets' is the page's no-param default (the legacy campus serve bucket,
  // kept for pre-split links); every real arrangement is explicit (PROD-1234).
  if (request.grouping !== 'buckets') {
    params.set('grouping', request.grouping)
  }
  if (request.rosterFilter && request.rosterFilter.labels.size > 0) {
    params.set('rosterAxis', request.rosterFilter.axis)
    for (const label of request.rosterFilter.labels) {
      params.append('rosterGroup', label)
    }
  }
  if (request.mealtimeFilter.size > 0) {
    params.set('mealtimes', [...request.mealtimeFilter].join(','))
  }
  if (request.orderColumnFilter.size > 0) {
    params.set('orderColumns', [...request.orderColumnFilter].join(','))
  }
  if (request.splitOrdersByBucket) {
    params.set('orderSplit', 'bucket')
  }
  return `/school/print?${params.toString()}`
}
